import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService, HttpMethod } from '../api/api.service';
import { CacheService } from '../cache/cache.service'; // Import CacheService

// Interfaces for User-related operations
export interface CreateUserRequest {
  EMAIL: string;
  FIRST_NAME: string;
  LAST_NAME: string;
  HOUSEHOLD_ID?: string;
  ORGANIZATION_ID: number;
  ORGANIZATION_GROUP_ID?: number;
  ROLE?: string;
}

export interface UpdateUserRequest {
  EMAIL: string;
  FIRST_NAME: string;
  LAST_NAME: string;
  HOUSEHOLD_ID?: number;
  /** Nullable — super admin / supervisor users can be unassigned from any organization (they work at the group level). */
  ORGANIZATION_ID: number | null;
  ROLE: string;
}

export interface User {
  USER_ID: number;
  COGNITO_ID: string;
  HOUSEHOLD_ID: number | null;
  EMAIL: string;
  FIRST_NAME: string;
  LAST_NAME: string;
  CREATED_AT: string;
  UPDATED_AT: string;
  ORGANIZATION_ID: number;
  /** Accepted values: 'SUPER_ADMIN' | 'SUPERVISOR' | 'MANAGER' | 'ADVISOR' | 'MEMBER'.
   *  MEMBER rows have COGNITO_ID=NULL and no login account. */
  ROLE: string;
  /** Present when API returns it (e.g. super admin list). */
  ORGANIZATION_GROUP_ID?: number | null;
  /** Populated by GET /users/:id — human-readable name. */
  ORGANIZATION_NAME?: string | null;
  /** Populated by GET /users/:id — human-readable name. */
  ORGANIZATION_GROUP_NAME?: string | null;
}

export interface Organization {
  ORGANIZATION_ID: number;
  ORGANIZATION_NAME: string;
  GROUP_ID?: number | null;
}

export interface OrganizationGroup {
  GROUP_ID: number;
  GROUP_NAME: string;
  DESCRIPTION?: string;
}

export interface OrgGroupWithOrgs {
  GROUP_ID: number;
  GROUP_NAME: string;
  members: Array<{
    ORGANIZATION_ID: number;
    organization?: {
      ORGANIZATION_ID: number;
      ORGANIZATION_NAME: string;
    };
  }>;
}

export interface UserQueryParams {
  page?: number;
  pageSize?: number;
  /** Single box: partial match on first name, last name, or email (OR). */
  search?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  organizationId?: number;
  householdId?: number;
  nonUserRolesOnly?: string;
  role?: string;
  /** Pass 'all' to bypass active-org scope (OSJ/admin pages). */
  scope?: 'all';
}

export interface GetUsersResponse {
  users: User[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface GetMeResponse {
  USER: User;
  ORGANIZATION: Organization;
  ORGANIZATION_GROUP?: OrganizationGroup;
  activeOrg?: Organization | null;
  activeGroup?: OrganizationGroup | null;
}

export interface AuthStatusResponse {
  is_started: boolean;
  is_in_progress: boolean;
  is_complete: boolean;
  created_by: string;
  created_by_id: number;
  last_login: Date;
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  /*
    This is such a long story... When the user logs in, there are a few different places
    that try to get information about the current user, which must ultimately be requested 
    here, via the API. I tried caching the response from this request, but so many requests
    were coming in around the same time that the original promise had not resolved before the 
    next function call came looking for the same response, so it would trigger again. The solution
    was to use a private variable to store the promise of the current request, and return that
    promise if it was already in flight. This ensures that only one request is made at a time,
    and all subsequent requests wait for the first to complete.
  */
  private currentUserRequestInFlight: Promise<GetMeResponse> | null = null;
  private readonly baseUrl = 'users'; // Base route for the users API

  private readonly currentUserSubject = new BehaviorSubject<GetMeResponse | null>(null);
  /** Latest resolved /users/me payload. Null before first successful load. Updated on cache-hit and API-resolve. */
  readonly currentUser$: Observable<GetMeResponse | null> = this.currentUserSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private cacheService: CacheService // Inject CacheService
  ) { }

  /**
   * Create a new user
   * @param userData The user data to create
   * @returns Promise with confirmation response
   */
  async createUser(userData: CreateUserRequest): Promise<any> {
    return this.apiService.call<any>(HttpMethod.POST, this.baseUrl, userData);
  }

  /**
   * List users with optional filters
   * @param params Optional query parameters for filtering
   * @returns Promise with user list response
   */
  async listUsers(params?: UserQueryParams): Promise<GetUsersResponse> {
    // Convert params object to URL search params
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const queryString = queryParams.toString();
    const url = `${this.baseUrl}${queryString ? `?${queryString}` : ''}`;
    
    return this.apiService.call<GetUsersResponse>(HttpMethod.GET, url);
  }

  async getById(userId: number): Promise<User> {
    return this.apiService.call<User>(HttpMethod.GET, `${this.baseUrl}/${userId}`);
  }

  /**
   * Update an existing user
   * @param userId The ID of the user to update
   * @param userData The updated user data
   * @returns Promise with confirmation response
   */
  async updateUser(userId: number, userData: UpdateUserRequest): Promise<any> {
    return this.apiService.call<any>(HttpMethod.PUT, `${this.baseUrl}/${userId}`, userData);
  }

  /**
   * Delete a user
   * @param userId The ID of the user to delete
   * @returns Promise with confirmation response
   */
  async deleteUser(userId: number): Promise<any> {
    return this.apiService.call<any>(HttpMethod.DELETE, `${this.baseUrl}/${userId}`);
  }

  async archiveUser(userId: number): Promise<unknown> {
    return this.apiService.call<unknown>(HttpMethod.PATCH, `${this.baseUrl}/${userId}/archive`, {});
  }

  async reassignUserOrganization(userId: number, organizationId: number): Promise<unknown> {
    return this.apiService.call<unknown>(HttpMethod.PATCH, `${this.baseUrl}/${userId}/organization`, {
      organizationId,
    });
  }

  /**
   * Get current user details based on their token
   * @returns Promise with current user and organization details
   */
  async getCurrentUser(): Promise<GetMeResponse> {

    // 1. Try to get from CacheService first
    const cachedUserDetails = this.cacheService.getCachedUserDetails();
    if (cachedUserDetails) {
      console.log('[UsersService] Returning cached user details from CacheService.');
      this.currentUserSubject.next(cachedUserDetails);
      return Promise.resolve(cachedUserDetails);
    }

    // 2. If a request is already in flight, return its promise
    if (this.currentUserRequestInFlight) {
      console.log('[UsersService] Request for user details already in flight, returning existing promise.');
      return this.currentUserRequestInFlight;
    }

    // 3. If not in cache and no request in flight, fetch from API
    console.log('[UsersService] No cached data and no request in flight. Fetching user details from API.');
    this.currentUserRequestInFlight = this.apiService.call<GetMeResponse>(HttpMethod.GET, `${this.baseUrl}/me`)
      .then(userDetails => {
        console.log('[UsersService] API call successful, caching user details.');
        this.cacheService.setCachedUserDetails(userDetails);
        this.currentUserSubject.next(userDetails);
        this.currentUserRequestInFlight = null; // Clear the in-flight request
        return userDetails;
      })
      .catch(error => {
        console.error('[UsersService] Error fetching user details from API:', error);
        this.cacheService.clearCachedUserDetails(); // Clear cache on error
        this.currentUserRequestInFlight = null; // Clear the in-flight request
        throw error; // Re-throw the error to be handled by the caller
      });

    return this.currentUserRequestInFlight;
  }

  async clearCachedUserAndRefresh(): Promise<void> {
    this.cacheService.clearCachedUserDetails();
    this.currentUserRequestInFlight = null;
    await this.getCurrentUser();
  }

  getCachedUserDetails(): GetMeResponse | null {
    return this.cacheService.getCachedUserDetails();
  }

  getCachedUserDetailsCachedAt(): number | null {
    return this.cacheService.getCachedUserDetailsCachedAt();
  }

  async getUserAuthStatus(userId: number): Promise<AuthStatusResponse> {
    return this.apiService.call<AuthStatusResponse>(HttpMethod.GET, `${this.baseUrl}/auth-status/${userId}`);
  }
}
